import { useApolloClient } from '@apollo/client';
import { useToast } from '@chakra-ui/react';
import { useCallback, useState } from 'react';

import {
  GET_AGENTV_ATTACHMENT_DOWNLOAD_SIGNED_URL,
  GET_AGENTV_ATTACHMENT_UPLOAD_SIGNED_URL,
  LIST_AGENTV_ATTACHMENTS,
} from './requests/agentV.gql';
import {
  GetAgentVAttachmentDownloadSignedUrlQueryGQLType,
  GetAgentVAttachmentDownloadSignedUrlQueryVariablesGQLType,
  ListAgentVAttachmentsQueryGQLType,
  ListAgentVAttachmentsQueryVariablesGQLType,
} from './requests/agentV.gql.generated';
import {
  errorToastConfig,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { presignedPostUpload } from 'packages/files/ui/utils';

/**
 * A file the user has attached to the AgentV chat. `key` is the exact S3 key the
 * upload returned (`agentv-attachments/<org>/<user>/<uuid>-<filename>`); the
 * download call sends it back verbatim and the server asserts it lives under the
 * caller's own prefix before signing. See `docs/AgentV_Graph_Authoring.md` §7a.
 */
export type AgentVAttachment = {
  filename: string;
  key: string;
};

export type AgentVAttachmentsState = {
  attachments: AgentVAttachment[];
  isUploading: boolean;
  isLoadingExisting: boolean;
  uploadFile: (file: File) => Promise<void>;
  loadExistingAttachments: () => Promise<void>;
  downloadAttachment: (attachment: AgentVAttachment) => void;
  removeAttachment: (key: string) => void;
};

// Upload query response shape (`queryResponseFields` is unused for this feature;
// the S3 key we need comes back as `s3FilePath`).
type UploadQueryVariables = { filename: string };

/**
 * Upload/download plumbing for AgentV chat attachments. Deliberately separate
 * from `useAgentVStream`: attachments are ordinary GraphQL/S3 calls, not part of
 * the SSE turn. Once uploaded, a file is visible to the agent via its
 * `list_files` tool (same ctx-derived prefix) — no wire-contract change needed.
 */
export const useAgentVAttachments = (): AgentVAttachmentsState => {
  const client = useApolloClient();
  const toast = useToast();
  const [attachments, setAttachments] = useState<AgentVAttachment[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isLoadingExisting, setIsLoadingExisting] = useState<boolean>(false);

  // Fetch the caller's previously-uploaded files (within the S3 TTL window) and
  // surface them as downloadable chips. Explicit/on-demand — the user asks for
  // this via a button rather than it firing on every mount. Merges with any
  // session uploads, deduping by key so it can't clobber a file just attached.
  const loadExistingAttachments = useCallback(async (): Promise<void> => {
    setIsLoadingExisting(true);
    try {
      const { data } = await client.query<
        ListAgentVAttachmentsQueryGQLType,
        ListAgentVAttachmentsQueryVariablesGQLType
      >({ query: LIST_AGENTV_ATTACHMENTS, fetchPolicy: 'network-only' });
      const existing = data.listAgentVAttachments ?? [];
      setAttachments((session) => {
        const byKey = new Map<string, AgentVAttachment>();
        existing.forEach((a) => byKey.set(a.key, a));
        // Session uploads win on key collision (they're the freshest).
        session.forEach((a) => byKey.set(a.key, a));
        return Array.from(byKey.values());
      });
    } catch {
      toast({
        ...errorToastConfig,
        title: 'Could not load your previous files.',
      });
    } finally {
      setIsLoadingExisting(false);
    }
  }, [client, toast]);

  const uploadFile = useCallback(
    async (file: File): Promise<void> => {
      setIsUploading(true);
      try {
        const { s3FilePath } = await presignedPostUpload<
          UploadQueryVariables,
          Record<string, unknown>
        >({
          client,
          file,
          query: GET_AGENTV_ATTACHMENT_UPLOAD_SIGNED_URL,
          queryVariables: { filename: file.name },
        });

        setAttachments((prev) => [
          ...prev,
          { filename: file.name, key: s3FilePath },
        ]);
        toast({ ...successToastConfig, title: `Attached ${file.name}` });
      } catch {
        toast({
          ...errorToastConfig,
          title: `Could not attach ${file.name}. Please try again.`,
        });
      } finally {
        setIsUploading(false);
      }
    },
    [client, toast],
  );

  const downloadAttachment = useCallback(
    (attachment: AgentVAttachment): void => {
      // Fire the signed-GET query imperatively (not a hook) so a single handler
      // can serve any chip. On success, trigger a browser download via a
      // transient anchor — the same pattern the finleyAi DownloadContainer uses.
      client
        .query<
          GetAgentVAttachmentDownloadSignedUrlQueryGQLType,
          GetAgentVAttachmentDownloadSignedUrlQueryVariablesGQLType
        >({
          query: GET_AGENTV_ATTACHMENT_DOWNLOAD_SIGNED_URL,
          variables: {
            serializedParams: JSON.stringify({ key: attachment.key }),
          },
          fetchPolicy: 'network-only',
        })
        .then(({ data }) => {
          const download = data.getSignedDownloadUrls?.[0];
          if (!download?.url) {
            throw new Error('No download URL returned.');
          }
          const anchor = document.createElement('a');
          anchor.href = download.url;
          anchor.download = download.filename ?? attachment.filename;
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
        })
        .catch(() => {
          toast({
            ...errorToastConfig,
            title: `Could not download ${attachment.filename}.`,
          });
        });
    },
    [client, toast],
  );

  const removeAttachment = useCallback((key: string): void => {
    // Local-only: drops the chip from the composer. The file stays in S3 (and
    // remains visible to `list_files`) until its lifecycle TTL expires — there
    // is no delete endpoint in v1.
    setAttachments((prev) => prev.filter((a) => a.key !== key));
  }, []);

  return {
    attachments,
    isUploading,
    isLoadingExisting,
    uploadFile,
    loadExistingAttachments,
    downloadAttachment,
    removeAttachment,
  };
};
